// Browser WebSocket transport for the Niu protocol.
//
// One client instance per connection attempt. Owns the seq tracker and emits
// catchup frames automatically when a gap appears. Decoded messages and
// connection-state transitions are delivered to the listener callbacks; the
// caller is responsible for passing them on to stores.

import { decodeMessage, encodeMessage, NiuDecodeError } from '../protocol/codec';
import type { NiuMessage } from '../protocol/types';
import { SeqTracker } from './SeqTracker';

export type ConnectionState =
  | { kind: 'disconnected' }
  | { kind: 'connecting' }
  | { kind: 'connected' }
  | { kind: 'error'; error: Error };

export interface WebSocketClientOptions {
  /** Base WSS URL — without query string. */
  url: string;
  token: string;
  deviceId: string;
  /** Highest seq the client already holds; the server replays anything after
   *  it. Defaults to 0 (full subscribe). */
  sinceSeq?: number;
  /** Streaming opt-in tokens. We default to text+thinking+tool_call. */
  streamCapabilities?: string[];
  /** Frame-shape capabilities. Default = `['batch', 'fetch_earlier']`. */
  features?: string[];
  /** Cold-connect tail cap (§1). Server default 250; `0` opts out. */
  initialLimit?: number;
  onMessage: (message: NiuMessage) => void;
  onState: (state: ConnectionState) => void;
  /** Schema-drift hook (known type, payload fails to decode). */
  onDecodeFailure?: (error: NiuDecodeError, raw: string) => void;
}

function buildURL(opts: WebSocketClientOptions): string {
  const u = new URL(opts.url);
  u.searchParams.set('token', opts.token);
  u.searchParams.set('device_id', opts.deviceId);
  if (opts.sinceSeq != null) u.searchParams.set('since_seq', String(opts.sinceSeq));
  const stream = opts.streamCapabilities ?? ['text', 'thinking', 'tool_call'];
  if (stream.length > 0) u.searchParams.set('stream', stream.join(','));
  const features = opts.features ?? ['batch', 'fetch_earlier'];
  if (features.length > 0) u.searchParams.set('features', features.join(','));
  if (opts.initialLimit != null) u.searchParams.set('initial_limit', String(opts.initialLimit));
  return u.toString();
}

/** Discriminator for `catchup_complete.more` auto-pagination. */
type ReplayRequestKind = 'initialTail' | 'deltaCatchup' | 'fetchEarlier';

export class WebSocketClient {
  private socket: WebSocket | null = null;
  private readonly tracker: SeqTracker;
  private closedExplicitly = false;
  private lastReplayRequestKind: ReplayRequestKind;
  /** §3.11 throttle — one in-flight fetch_earlier per socket. */
  private fetchEarlierInflight = false;

  constructor(private readonly opts: WebSocketClientOptions) {
    this.tracker = new SeqTracker(opts.sinceSeq ?? 0);
    this.lastReplayRequestKind = (opts.sinceSeq ?? 0) === 0 ? 'initialTail' : 'deltaCatchup';
  }

  connect(): void {
    if (this.socket != null) return;
    this.opts.onState({ kind: 'connecting' });

    let socket: WebSocket;
    try {
      socket = new WebSocket(buildURL(this.opts));
    } catch (err) {
      this.opts.onState({ kind: 'error', error: err as Error });
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.opts.onState({ kind: 'connected' });
    };

    socket.onmessage = (ev) => {
      this.handleRaw(typeof ev.data === 'string' ? ev.data : '');
    };

    socket.onerror = () => {
      // The browser hides the underlying error; surface a generic one. The
      // accompanying `close` event arrives right after.
      this.opts.onState({ kind: 'error', error: new Error('WebSocket error') });
    };

    socket.onclose = () => {
      this.socket = null;
      if (!this.closedExplicitly) {
        this.opts.onState({ kind: 'disconnected' });
      }
    };
  }

  disconnect(): void {
    this.closedExplicitly = true;
    const s = this.socket;
    this.socket = null;
    if (s) {
      try {
        s.close(1000, 'client disconnect');
      } catch {
        /* ignore */
      }
    }
    this.opts.onState({ kind: 'disconnected' });
  }

  send(message: NiuMessage): boolean {
    const s = this.socket;
    if (!s || s.readyState !== WebSocket.OPEN) return false;
    try {
      s.send(encodeMessage(message));
      return true;
    } catch {
      return false;
    }
  }

  /** §3.11 — `false` if already in flight or socket closed. */
  fetchEarlier(beforeSeq: number, limit?: number): boolean {
    if (this.fetchEarlierInflight) return false;
    const ok = this.send({
      id: cryptoRandomId('niu-fetch-earlier'),
      type: 'fetch_earlier_messages',
      payload: { kind: 'fetch_earlier_messages', data: { beforeSeq, limit } },
    });
    if (ok) {
      this.fetchEarlierInflight = true;
      this.lastReplayRequestKind = 'fetchEarlier';
    }
    return ok;
  }

  // ---- internal ----

  private dispatch(msg: NiuMessage): void {
    const outcome = this.tracker.note(msg.seq);
    if (outcome.kind === 'requestCatchup') {
      this.lastReplayRequestKind = 'deltaCatchup';
      this.send({
        id: cryptoRandomId('niu-catchup'),
        type: 'catchup',
        payload: {
          kind: 'catchup',
          data: { sinceSeq: outcome.sinceSeq, limit: outcome.limit },
        },
      });
    }

    if (msg.type === 'catchup_complete') {
      this.tracker.noteCatchupComplete();
      // §4.10: auto-paginate `more=true` only on delta catchups.
      if (msg.payload.kind === 'catchup_complete' && msg.payload.data.more && this.lastReplayRequestKind === 'deltaCatchup') {
        this.send({
          id: cryptoRandomId('niu-catchup'),
          type: 'catchup',
          payload: {
            kind: 'catchup',
            data: { sinceSeq: this.tracker.highestContiguous },
          },
        });
      }
    }

    if (msg.type === 'earlier_complete') {
      this.fetchEarlierInflight = false;
    }

    if (msg.type === 'error' && msg.payload.kind === 'error' && msg.payload.data.code === 'fetch_earlier_busy') {
      this.fetchEarlierInflight = false;
    }

    this.opts.onMessage(msg);
  }

  private handleRaw(text: string): void {
    if (!text) return;
    let msg: NiuMessage | null;
    try {
      msg = decodeMessage(text);
    } catch (err) {
      if (err instanceof NiuDecodeError) {
        this.opts.onDecodeFailure?.(err, text);
      } else {
        // eslint-disable-next-line no-console
        console.error('[niu] decode failure', err);
      }
      return;
    }
    if (msg == null) return;

    // §4.13: unfold message_batch in the transport.
    if (msg.payload.kind === 'message_batch') {
      for (const inner of msg.payload.data.envelopes) this.dispatch(inner);
      return;
    }
    this.dispatch(msg);
  }
}

function cryptoRandomId(prefix: string): string {
  // Browsers expose `crypto.randomUUID` in secure contexts (HTTPS / localhost).
  // Fall back to a Math.random-based id elsewhere.
  const c = (globalThis as unknown as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return `${prefix}-${c.randomUUID()}`;
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${Date.now().toString(36)}-${rand}`;
}
